import { useEffect, useRef, useState } from 'react'
import { previewNextInvoiceNumber } from '@/api/invoices.api'
import { toPeriod } from '@/helper/invoice.helpers'
import type { CreateInvoicePayload, InvoiceSelectableActivity } from './invoice.types'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** `YYYY-MM-DD` and a real calendar date — what the backend's `LocalDate` param accepts. */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false
  const [y, m, d] = value.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

/**
 * The period the dialog will send as `periodStart` / `periodEnd` on draft creation, derived the
 * same way for every billing mode. Both are `''` while the form cannot yet produce them (no
 * month, no range, no selected activity). The invoice-number preview must use exactly this
 * `periodStart`, because the backend fills `{YYYY}` / `{YY}` / `{MM}` from it, not from today.
 */
export function resolveSubmissionPeriod(
  values: Pick<
    CreateInvoicePayload,
    'mode' | 'billingMonth' | 'billingYear' | 'periodStart' | 'periodEnd' | 'selectedActivityIds'
  >,
  activities: Pick<InvoiceSelectableActivity, 'id' | 'date'>[]
): { periodStart: string; periodEnd: string } {
  if (values.mode === 'MONTH') {
    const year = Number(values.billingYear)
    const month = Number(values.billingMonth)
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
      return { periodStart: '', periodEnd: '' }
    }
    return toPeriod(year, month)
  }

  if (values.mode === 'DATE_RANGE') {
    return {
      periodStart: String(values.periodStart ?? ''),
      periodEnd: String(values.periodEnd ?? ''),
    }
  }

  const selectedIds = values.selectedActivityIds ?? []
  const selected = activities
    .filter(a => selectedIds.includes(a.id))
    .sort((a, b) => a.date.localeCompare(b.date))

  if (!selected.length) return { periodStart: '', periodEnd: '' }

  return { periodStart: selected[0].date, periodEnd: selected[selected.length - 1].date }
}

export type InvoiceNumberPreview =
  | { status: 'idle'; invoiceNumber: null }
  | { status: 'loading'; invoiceNumber: null }
  | { status: 'success'; invoiceNumber: string }
  | { status: 'error'; invoiceNumber: null }

const IDLE: InvoiceNumberPreview = { status: 'idle', invoiceNumber: null }
const LOADING: InvoiceNumberPreview = { status: 'loading', invoiceNumber: null }
const ERROR: InvoiceNumberPreview = { status: 'error', invoiceNumber: null }

export type UseNextInvoiceNumberPreviewOptions = {
  /**
   * Fetch only while this is true: dialog open, create mode, automatic numbering enabled and
   * "Andere Rechnungsnummer" off. Flipping it back to true (dialog reopened, custom number
   * switched off again) always requests a fresh snapshot.
   */
  enabled: boolean
  /** The `periodStart` the draft will be created with (`YYYY-MM-DD`). */
  periodStart: string
  /** Owner of the invoice when previewing on someone's behalf (ADMIN route); omit for self. */
  ownerUserId?: string
}

/**
 * Best-effort preview of the number the next draft would receive (SIB-944).
 *
 * The result is keyed by owner and `periodStart`: every change starts a new request and shows
 * the loading state, and a response is applied only if it belongs to the latest request, so a
 * slow answer for an earlier period or owner can never overwrite the current one. Disabling the
 * hook resets to `idle` immediately, which also discards any response still in flight. Nothing
 * here writes to the form: the preview is display-only and never becomes the submitted number.
 */
export function useNextInvoiceNumberPreview({
  enabled,
  periodStart,
  ownerUserId,
}: UseNextInvoiceNumberPreviewOptions): InvoiceNumberPreview {
  const [state, setState] = useState<InvoiceNumberPreview>(IDLE)
  const latestRequest = useRef(0)

  const active = enabled && isIsoDate(periodStart)

  useEffect(() => {
    const requestId = ++latestRequest.current

    if (!active) {
      setState(IDLE)
      return
    }

    let cancelled = false
    setState(LOADING)

    previewNextInvoiceNumber(periodStart, ownerUserId).then(
      invoiceNumber => {
        if (cancelled || requestId !== latestRequest.current) return
        setState(invoiceNumber ? { status: 'success', invoiceNumber } : ERROR)
      },
      () => {
        if (cancelled || requestId !== latestRequest.current) return
        setState(ERROR)
      }
    )

    return () => {
      cancelled = true
    }
  }, [active, periodStart, ownerUserId])

  return state
}

export const INVOICE_NUMBER_PLACEHOLDER = '—'

export type InvoiceNumberDisplay =
  | { kind: 'value'; text: string }
  | { kind: 'loading' }
  | { kind: 'unavailable'; text: string }

/**
 * What the "Rechnungsnummer" line shows. Precedence: the user's own number while "Andere
 * Rechnungsnummer" is on, the draft's real number in edit mode, otherwise the backend preview
 * (SIB-944). There is deliberately no fabricated fallback: without a preview (numbering disabled,
 * period not chosen yet, request failed) the line shows a neutral placeholder.
 */
export function resolveInvoiceNumberDisplay({
  mode,
  useCustomInvoiceNumber,
  customInvoiceNumber,
  draftInvoiceNumber,
  autoNumberingEnabled,
  preview,
}: {
  mode: 'create' | 'edit'
  useCustomInvoiceNumber: boolean
  customInvoiceNumber?: string | null
  draftInvoiceNumber?: string | null
  autoNumberingEnabled: boolean
  preview: InvoiceNumberPreview
}): InvoiceNumberDisplay {
  if (useCustomInvoiceNumber) {
    return { kind: 'value', text: customInvoiceNumber?.trim() || INVOICE_NUMBER_PLACEHOLDER }
  }

  if (mode === 'edit') {
    return { kind: 'value', text: draftInvoiceNumber?.trim() || INVOICE_NUMBER_PLACEHOLDER }
  }

  if (!autoNumberingEnabled) {
    return { kind: 'value', text: INVOICE_NUMBER_PLACEHOLDER }
  }

  switch (preview.status) {
    case 'loading':
      return { kind: 'loading' }
    case 'success':
      return { kind: 'value', text: preview.invoiceNumber }
    case 'error':
      return { kind: 'unavailable', text: INVOICE_NUMBER_PLACEHOLDER }
    default:
      return { kind: 'value', text: INVOICE_NUMBER_PLACEHOLDER }
  }
}
